import React, { useState } from 'react';
import styled from '@emotion/styled';
import ReCAPTCHA from 'react-google-recaptcha';
import queryString from 'query-string';
import { saveContact } from './hsAPI';
import { useLocation } from "@reach/router"
import hrmssurvey from '../../../static/pillar/payroll/hrms/hrms-survey.png';
import {
	Box,
	Heading,
	Image,
	Text,
	Label,
	Radio,
	Flex,
	Input,
	Button,
	Spinner,
} from '@theme-ui/components';

const ShowProcessing = () => (
	<Box sx={{ mx: 1 }}>
		<Spinner color='orange2' size='24' />
	</Box>
);

let recaptchaRef = React.createRef();

const DownloadBox = () => {
	return (
		<Box sx={{ color: 'white', fontSize: 1, width: 302, pl: 4 }}>
			<Box sx={{ fontWeight: 'bold', my: 3 }}>Thank you for submiting.</Box>
			<Box>
				Get access to your free eGuide-"Human Resources Policy Manual for Small
				Business"
			</Box>
			<a href='https://www.datocms-assets.com/40521/1657868520-hr-policy-eguide-greythr.pdf?dl=HR-Policy-eGuide-greytHR'>
				<Button
					sx={{
						bg: 'white',
						color: 'orange1',
						mt: 4,
						fontWeight: 600,
						'&:hover': { bg: 'white', color: 'orange1' },
					}}>
					Download Now
				</Button>
			</a>
		</Box>
	);
};

const SurveyForm = ({ setShowDownloadBtn }) => {
	const location = useLocation();
	const searchParams = queryString.parse(location.search);
	const utm_source = searchParams['utm_source'];
	const utm_medium = searchParams['utm_medium'];
	const utm_campaign = searchParams['utm_campaign'];
	
	const [selectedSurveyOption, setSelectedSurveyOption] = useState();
	const [email, setEmail] = useState();
	const [hasError, setHasError] = useState();
	const [message, setMessage] = useState('');
	const [processing, setProcessing] = useState(false);
	const [validEmail, setValidEmail] = useState(true);

	const onEmailChange = (e) => {
		setValidEmail(true);
		const email = e.target.value;
		setEmail(email);

		if (isNaN(email)) {
			if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,10}$/i.test(email)) {
				setValidEmail(false);
			}
		}
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setHasError(false);
		setProcessing(true);
		setMessage('');

		if (recaptchaRef && recaptchaRef.current) {
			let captcha = '';
			recaptchaRef.current.reset();
			captcha = await recaptchaRef.current.executeAsync();
			const formData = {
				hs_email: email,
				hs_qww: selectedSurveyOption,
				hubspot_form_code: 'HRMS_SURVEY_EGUIDE_DOWNLOAD_FORM',
				invisible_recaptcha: true,
				captcha,
				page_code: 'Guide to HRMS',
				page_name: 'Guide to HRMS',
				page_url: location?.href,
				utm_campaign,
				utm_medium,
				utm_source
			};
			if (captcha) {
				saveContact(
					formData,
					(response) => {
						setProcessing(false);
						setShowDownloadBtn(true);
					},
					(error) => {
						setProcessing(false);
						if (
							error.response &&
							error.response.status == 400 &&
							error.response.data.invalid_email
						) {
							setEmailError(error.response.data.invalid_email);
						} else {
							setHasError(true);
						}
					}
				);
			} else {
				setProcessing(false);
				setHasError(true);
			}
		} else {
			setProcessing(false);
			setHasError(true);
		}
	};

	return (
		<Box sx={formStyles} className='hs-form' as='form' onSubmit={handleSubmit}>
			<Box>
				<Box mb={2}>
					<strong>
						Which of the below is the most crucial for you in setting up a
						reliable HR process in your organization?
					</strong>
					<Box
						sx={{
							fontSize: '0.688rem',
							mt: 2,
							pb: 1,
							borderBottom: '1px solid',
						}}>
						Select the most important as per your organization
					</Box>
				</Box>
				<Label sx={{ fontWeight: 'semibold' }}>
					<StyledRadio
						name='survey-options'
						onClick={(e) => setSelectedSurveyOption(1)}
						required
					/>
					Having a well-defined HR policy in place
				</Label>
				<Label sx={{ fontWeight: 'semibold' }}>
					<StyledRadio
						name='survey-options'
						onClick={(e) => setSelectedSurveyOption(2)}
					/>
					Having an error free Payroll Process
				</Label>
				<Label sx={{ fontWeight: 'semibold' }}>
					<StyledRadio
						name='survey-options'
						onClick={(e) => setSelectedSurveyOption(3)}
					/>
					Implementing leave & attendance policies and ensuring adherence
				</Label>
				<Label sx={{ fontWeight: 'semibold' }}>
					<StyledRadio
						name='survey-options'
						onClick={(e) => setSelectedSurveyOption(4)}
					/>
					Employee engagement practices and activities
				</Label>
				<Label sx={{ fontWeight: 'semibold' }}>
					<StyledRadio
						name='survey-options'
						onClick={(e) => setSelectedSurveyOption(5)}
					/>
					Having an insightful dashboard for the top management
				</Label>
				<StyledInput
					type='email'
					value={email}
					placeholder='Enter your work email*'
					name='email'
					required
					maxLength='100'
					onChange={onEmailChange}
				/>
				<ReCAPTCHA
					ref={recaptchaRef}
					size='invisible'
					sitekey={process.env.GATSBY_INVISIBLE_RE_CAPTCHA_SITE_KEY}
					badge='bottomleft'
				/>
				<Button>Submit</Button>
				<Box className='msg-box'>
					{message}
					{hasError && 'Sorry, some error seems to have occurred!'}
					{processing ? <ShowProcessing /> : null}
					{!validEmail && 'Invalid Email!'}
				</Box>
			</Box>
		</Box>
	);
};

const HrmsSurvey = () => {
	const [showDownloadBtn, setShowDownloadBtn] = useState(false);

	return (
		<Flex
			sx={{
				flexDirection: 'column',
				alignItems: 'center',
				bg: 'primary2',
				my: [2, 4],
				px: [1, 3, 5],
				py: 4,
				minHeight: 480,
			}}>
			<Heading
				as='h3'
				variant='extralightHeading'
				sx={{ color: 'white', mb: 4 }}>
				Download your free HR eGuide here
				<Text sx={{ display: 'inline', fontWeight: 'normal' }}>
					{' '}
					by participating in our survey
				</Text>
			</Heading>
			<Flex sx={{ flexDirection: ['column', 'row'] }}>
				<Box sx={{ width: 290 }}>
					<Image sx={{ width: '100%' }} src={hrmssurvey} alt='HRMS Survey' />
				</Box>
				{showDownloadBtn ? (
					<DownloadBox />
				) : (
					<SurveyForm setShowDownloadBtn={setShowDownloadBtn} />
				)}
			</Flex>
		</Flex>
	);
};

const StyledRadio = styled(Radio)`
	color: white;
	input:checked {
		color: white !important;
		background-color: white;
	}
`;

const StyledInput = styled(Input)``;

const formStyles = {
	width: 302,
	color: 'white',
	fontSize: '0.813rem',
	label: {
		fontSize: '0.813rem',
	},
	ml: [0, 4],
	button: {
		bg: 'orange1',
		width: '100%',
		'&:hover': {
			bg: 'orange1',
		},
	},
	input: {
		bg: 'white',
		mb: 2,
		mt: 3,
		borderRadius: 2,
		'&:focus': {
			outline: 'none',
			border: 'none',
		},
	},
	'input:checked': {
		color: 'white',
	},
	'.msg-box': {
		fontWeight: 'bold',
		mt: 3,
		textAlign: 'center',
	},
};

export default HrmsSurvey;
